import { useCallback, useRef, useState } from "react";

/** Return this from the action to keep the button locked after it finishes, e.g. when redirecting away. */
export const KEEP_LOCKED = Symbol("keepLocked");

/**
 * Guards a submit handler against double clicks. The lock is a ref, not state: a state update only
 * lands on the next render, so two quick clicks would both get through. `submitting` drives the
 * button (see SubmitButton). The lock is released when the action ends, even if it throws.
 *
 * Usage: const { run, submitting } = useSubmitAction(async (e) => { ... });  <form onSubmit={run}>
 */
export const useSubmitAction = <A extends unknown[]>(action: (...args: A) => Promise<unknown> | unknown) => {
  const lockRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  const run = useCallback(
    async (...args: A) => {
      // a blocked click on a form must still not do the browser's native submit (page reload)
      const event = args[0] as { preventDefault?: () => void } | undefined;
      event?.preventDefault?.();

      if (lockRef.current) return;
      lockRef.current = true;
      setSubmitting(true);
      let keepLocked = false;
      try {
        keepLocked = (await action(...args)) === KEEP_LOCKED;
      } finally {
        if (!keepLocked) {
          lockRef.current = false;
          setSubmitting(false);
        }
      }
    },
    [action],
  );

  return { run, submitting };
};
